import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbars } from '@customerio/pluma-components/react';
import type { PushPlatform } from '@/features/push-credentials';
import { getApiClientErrorDetailFromError } from '@/services/api/errors';
import { appKeys, type App } from '@/services/db/app/entity';
import { getAppQueryOptions } from '@/services/db/app/queries';
import { applySavePlan, ensureApp, SAVE_FAILED_MESSAGE, type SavePlan } from '../utils/save-app';

export type UseAppSaveArgs = {
  workspaceId: number;
  savedApp: App | null;
  /** Called only once the app has been re-read and agrees the credential is written. */
  onCredentialPersisted: (platform: PushPlatform) => void;
};

/**
 * A create that fails part-way has still registered the app, so the record it
 * made is kept and a retry edits it rather than adding a second. With no
 * transaction behind the sequence, both outcomes re-read the app.
 */
export function useAppSave({ workspaceId, savedApp, onCredentialPersisted }: UseAppSaveArgs) {
  const qc = useQueryClient();
  const snackbars = useSnackbars();
  const [createdApp, setCreatedApp] = useState<App | null>(null);
  const [error, setError] = useState<string | null>(null);

  // What the sequence reads: set the moment the record exists, before React
  // has re-rendered with it.
  const createdAppRef = useRef<App | null>(null);

  const persistedRef = useRef<PushPlatform[]>([]);

  const rememberCreatedApp = (created: App) => {
    createdAppRef.current = created;
    setCreatedApp(created);
  };

  const reload = async (appId: number): Promise<App | null> => {
    try {
      const refreshed = await qc.fetchQuery(getAppQueryOptions(workspaceId, appId));
      await qc.invalidateQueries({ queryKey: appKeys.list(workspaceId) });
      return refreshed;
    } catch {
      // The save's own outcome is already on screen; a follow-up read that
      // fails adds nothing the user can act on.
      return null;
    }
  };

  const mutation = useMutation({
    mutationFn: async (plan: SavePlan) => {
      persistedRef.current = [];
      const target = await ensureApp(workspaceId, savedApp ?? createdAppRef.current, plan.name);
      if (savedApp == null) {
        rememberCreatedApp(target);
      }
      await applySavePlan({ workspaceId, appId: target.id }, plan, (platform) => {
        persistedRef.current.push(platform);
      });
      const refreshed = await reload(target.id);
      if (savedApp == null && refreshed != null) {
        rememberCreatedApp(refreshed);
      }
      return refreshed ?? target;
    },
    onMutate: () => {
      setError(null);
    },
    onError: async (error: unknown) => {
      setError(SAVE_FAILED_MESSAGE);
      // A 4xx the server explained (a duplicate identifier, a key it could not
      // use) is a correctable mistake, and only its explanation says which.
      const detail = getApiClientErrorDetailFromError(error);
      if (detail != null) {
        snackbars.create({ variant: 'critical', id: 'app_save.error', message: detail });
      }

      const current = savedApp ?? createdAppRef.current;
      if (current == null) {
        return;
      }
      const refreshed = await reload(current.id);
      if (refreshed == null) {
        // The app on screen is stale, so nothing here can be called written.
        return;
      }
      if (savedApp == null) {
        rememberCreatedApp(refreshed);
      }
      for (const platform of persistedRef.current) {
        onCredentialPersisted(platform);
      }
    },
  });

  const app = savedApp ?? createdApp;

  return {
    app,
    error,
    clearError: () => setError(null),
    isSaving: mutation.isPending,
    save: async (plan: SavePlan): Promise<App | null> => {
      try {
        return await mutation.mutateAsync(plan);
      } catch {
        // Reported through the form's own error line.
        return null;
      }
    },
  };
}
